import React, { useEffect, useState } from 'react'
import { getIconUrl } from '../../services/static_data'
import { useStoreActions } from '../../model/hooks'
import { useNavigate } from 'react-router'
import { formatNumber } from '../../blockchainUtils/math'
import BigNumber from 'bignumber.js'
import { RewardBadgeIcon } from '../../svgIcons/RewardBadgeIcon'
import { PoolData, PoolOverview, loadPoolOverviewData, loadPoolsData } from '../charts/chartsApi'
import { formatDollarValue } from '../charts/chartUtils'
import { PrecentChange } from '../charts/PrecentChange'
import PrimaryButton from '../PrimaryButton'
import { useWalletContext } from '../WalletContext'
import { XIcon } from '../../svgIcons/XIcon'
import { ArrowDown } from '../../svgIcons/ArrowDownIcon'
import { CustomBreakpoints, useCustomBreakpoint } from '../charts/Charts'
import { GAtrack } from '../../services/ga'
import { possiblePools } from '../PositionsV3'

const allHeaderCells = [
    { colName: 'POOL', title: 'Eligible Pools', helptip: undefined },
    { colName: 'TVL', title: 'TVL', helptip: undefined },
    { colName: 'TOTAL_APR', title: 'Total APR', helptip: undefined },
    { colName: 'CLAIM', title: 'CLAIM', helptip: undefined }
]

export const IncentivePoolsTable = ({ tokenAddress, setIsWrongNetworkPopupActive }: { tokenAddress?: string; setIsWrongNetworkPopupActive: () => void }) => {
    const { walletAddress: connectedAddress, connect, networkName: network } = useWalletContext()
    const screenSize = useCustomBreakpoint()
    const navigate = useNavigate()
    const loadTokens = useStoreActions((actions) => actions.tokens.loadTokens)
    const [data, setData] = useState([] as (PoolOverview & PoolData)[])
    const [poolOverviews, setPoolsOverview] = useState(undefined as unknown as (PoolOverview & PoolData)[])
    const [searchTerm, setSearchTerm] = useState('')
    const [isInputFocused, setIsInputFocused] = useState(false)
    const [isNoResults, setIsNoResults] = useState(false)
    const [poolsList, setPoolsList] = useState({} as Record<string, (PoolOverview & PoolData)[]>)
    const [incentivePoolsData, setInceintivePoolsData] = useState([] as (PoolOverview & PoolData)[])
    const [openAPRList, setOpenAPRList] = useState({} as Record<string, boolean>)
    const [mainPools, setMainPools] = useState([] as string[])
    const [columns, setColumns] = useState([] as string[])

    useEffect(() => {
        if (poolOverviews) updateData()
    }, [poolOverviews])

    useEffect(() => {
        updateData()
    }, [searchTerm])

    useEffect(() => {
        const newCols = getColumns()
        setColumns(newCols)
    }, [screenSize])

    useEffect(() => {
        if (openAPRList && Object.keys(openAPRList).length > 0) sortPoolsData(poolOverviews)
    }, [openAPRList])

    useEffect(() => {
        loadTokens({ network: 'mainnet-alpha' })
    }, [])

    useEffect(() => {
        if (possiblePools.length > 0) loadAllPoolsData()
        // loadOverview()
    }, [tokenAddress, possiblePools])

    async function loadAllPoolsData() {
        const response = await loadPoolsData()

        const promises: any[] = []
        response.map((pool) => promises.push(loadPoolOverviewData(pool.poolkey)))

        const overviews: PoolOverview[] = await Promise.all(promises)
        const combinedData: any[] = []
        response.map((item, index) => {
            const isMatch = possiblePools.some((pool) => [item.token0.address, item.token1.address].every((token) => pool.includes(token)))
            if (!isMatch) return

            if ((tokenAddress && item.token0.address.toLowerCase() === tokenAddress?.toLowerCase()) || item.token1.address.toLowerCase() === tokenAddress?.toLowerCase())
                combinedData.push({ ...overviews[index], ...response[index] })
            else if (!tokenAddress) combinedData.push({ ...overviews[index], ...response[index] })
        })

        setPoolsOverview(combinedData)
        sortPoolsData(combinedData)
    }

    function sortPoolsData(data: (PoolOverview & PoolData)[]) {
        const sortedPoolsData = {} as Record<string, (PoolOverview & PoolData)[]>
        const dataCopy = [...data]
        dataCopy.sort((a, b) => {
            const aAPR = a.apr && a.apr.apr_cl ? a.apr.apr_cl : 0
            const aInceintiveAPR = a.apr && a.apr.incentive_apr ? a.apr.incentive_apr : 0
            const bAPR = b.apr && b.apr.apr_cl ? b.apr.apr_cl : 0
            const bInceintiveAPR = b.apr && b.apr.incentive_apr ? b.apr.incentive_apr : 0
            const totalBAPR = bAPR + bInceintiveAPR
            const totalAAPR = aAPR + aInceintiveAPR
            if (totalAAPR > totalBAPR) return -1
            if (totalBAPR > totalAAPR) return 1
            return 0
        })

        dataCopy.forEach((item) => {
            if (!sortedPoolsData[`${item.token0.address}_${item.token1.address}`]) return (sortedPoolsData[`${item.token0.address}_${item.token1.address}`] = [{ ...item }])
            sortedPoolsData[`${item.token0.address}_${item.token1.address}`].push(item)
        })

        setPoolsList(sortedPoolsData)
        const mainPoolKeys = [] as string[]
        const incentiveMainPools = [] as (PoolOverview & PoolData)[]

        Object.values(sortedPoolsData).map((item, index) => {
            mainPoolKeys.push(item[0].poolkey)
            const isOpen = openAPRList && openAPRList[`${item[0].token0.address}_${item[0].token1.address}`]
            if (isOpen) {
                return item.forEach((it) => {
                    incentiveMainPools.push(it)
                })
            } else return incentiveMainPools.push(item[0])
        })
        setMainPools(mainPoolKeys)

        setInceintivePoolsData(incentiveMainPools)
    }

    function updatePoolsWithoutBannedTokens(data: (PoolOverview & PoolData)[]) {
        const newData = [] as (PoolOverview & PoolData)[]

        data.map((item) => {
            if (item.poolkey.toLowerCase() !== '0x452add90bc93d620f2ea539fa16276b437445314e76ed23b17ac2e67395c99'.toLowerCase()) newData.push(item)
        })
        setData(newData)
    }

    function updateData() {
        if (!poolOverviews) return

        let overviews = [...poolOverviews]
        if (searchTerm) {
            const searchTermLowerCased = searchTerm.toLowerCase()
            overviews = poolOverviews.filter(
                (option) =>
                    option.poolkey.toLowerCase().includes(searchTermLowerCased) ||
                    option.token0.symbol.toLowerCase().includes(searchTermLowerCased) ||
                    option.token0.address.toLowerCase().includes(searchTermLowerCased) ||
                    option.token1.symbol.toLowerCase().includes(searchTermLowerCased) ||
                    option.token1.address.toLowerCase().includes(searchTermLowerCased)
            )
        }
        const poolsData: (PoolOverview & PoolData)[] = [...overviews]
        poolsData.sort((a, b) => {
            if (a.tvl > b.tvl) return -1
            if (b.tvl > a.tvl) return 1
            return 0
        })

        if (poolsData.length === 0) setIsNoResults(true)
        else setIsNoResults(false)

        if (!searchTerm) return updatePoolsWithoutBannedTokens(poolsData)
        else setData(poolsData)
    }

    function getColumns() {
        if (screenSize >= CustomBreakpoints.Large) return ['POOL', 'TVL', 'TOTAL_APR', 'CLAIM']
        // return ['Ranking', 'Account', 'Total Points']
        return ['POOL', 'TOTAL_APR', 'CLAIM']
    }

    function getToken1Name(pool: PoolOverview & PoolData) {
        const symbol = pool.token0.symbol === 'WSTETH' ? 'wstETH' : pool.token0.symbol
        return symbol
    }

    function getToken2Name(pool: PoolOverview & PoolData) {
        const symbol = pool.token1.symbol === 'WSTETH' ? 'wstETH' : pool.token1.symbol
        return symbol
    }

    function renderIncentiveBadge(token1: string, token2: string) {
        const isMatch = possiblePools.some((pool) => [token1, token2].every((token) => pool.includes(token)))

        if (!isMatch) return null
        return (
            <div className='relative flex items-center ml-2'>
                <div className='flex'>
                    <RewardBadgeIcon className='lg:w-[24px] lg:h-[24px] w-[12px] h-[12px] mr-2' />
                    {/* <span
                        style={{ background: 'rgba(255, 255, 255, 0.85' }}
                        className='w-[180px] left-[17px] group-hover:visible invisible group-hover:opacity-100 transition-opacity text-sm text-black rounded-md absolute opacity-0 top-[-36px] p-3 z-[1]'>
                        Eligible for incentive
                    </span> */}
                </div>
            </div>
        )
    }

    function getTableHeaderCellNames(): { title: string; helptip?: string }[] {
        const existingColumns = columns
        const columnsToUse: { title: string; helptip?: string | undefined }[] = []

        existingColumns.map((existingColumnName, index) => {
            const col = allHeaderCells.find((item) => item.colName === existingColumnName)
            if (col) columnsToUse.push(col)
        })

        //console.log("YSYS existingColumns=",existingColumns," columnsToUse=",columnsToUse)
        return columnsToUse
    }

    function renderHeaderTH(headerInfo: { title: string; helptip?: string }, index: number) {
        return (
            <th className={`text-white text-[12px] lg:text-[18px] font-[600] ${index === 0 ? 'lg:pl-[50px] pl-[16px]' : ''}`} key={headerInfo.title}>
                {headerInfo.title !== 'CLAIM' ? (
                    <div className='flex relative items-center lg:mr-0 mr-6'>
                        <span>{headerInfo.title}</span>
                        <div className='ml-2 group inline-flex'>
                            {/* {headerInfo.helptip && <HelptipIcon className='text-[#8387A5] w-4 h-4' />} */}
                            <span className='left-[-100px] lg:left-[-20px] top-[-45px] w-[200px] group-hover:visible invisible group-hover:opacity-100 transition-opacity bg-[#242C58] text-sm text-gray-100 rounded-md absolute opacity-0 top-[30px] p-3 z-[1]'>
                                {headerInfo.helptip}
                            </span>
                        </div>
                    </div>
                ) : null}
            </th>
        )
    }

    function renderTableHeader() {
        const headerColumns = getTableHeaderCellNames()
        const headerTHS = headerColumns.map(renderHeaderTH)
        return (
            <thead className=''>
                <tr className='border-b-[20px] border-transparent'>{headerTHS}</tr>
            </thead>
        )
    }

    function renderLoadingRow() {
        return (
            <tr className='border-[#0C3F6D] border-b-[1px]'>
                <td>
                    <div style={{ background: 'rgba(217, 217, 217, 0.14)' }} className='lg:ml-[50px] ml-[16px] h-[23px] w-[80px] lg:w-[256px] animate-pulse rounded-[2px]' />
                </td>
                <td>
                    <div style={{ background: 'rgba(217, 217, 217, 0.14)' }} className='h-[23px] w-[80px] lg:w-[114px] animate-pulse rounded-[2px]' />
                </td>
                <td className='hidden lg:flex'>
                    <div className='flex items-center'>
                        <div style={{ background: 'rgba(217, 217, 217, 0.14)' }} className='h-[23px] w-[80px] animate-pulse rounded-[2px]' />
                        <div style={{ background: 'rgba(217, 217, 217, 0.14)' }} className='h-[23px] w-[1px] animate-pulse mx-3' />
                        <div className='flex flex-col'>
                            <div style={{ background: 'rgba(217, 217, 217, 0.14)' }} className='h-[14px] lg:w-[164px] w-[80px] mb-2 animate-pulse rounded-[2px]' />
                            <div style={{ background: 'rgba(217, 217, 217, 0.14)' }} className='h-[14px] lg:w-[164px] w-[80px] animate-pulse rounded-[2px]' />
                        </div>
                    </div>
                </td>
                <td>
                    <div className='flex items-center'>
                        <div style={{ background: 'rgba(217, 217, 217, 0.14)' }} className='h-[23px] w-[80px] animate-pulse rounded-[2px]' />
                        <div style={{ background: 'rgba(217, 217, 217, 0.14)' }} className='h-[23px] w-[80px] animate-pulse rounded-[2px] ml-2' />
                    </div>
                </td>
            </tr>
        )
    }

    function renderTableBody() {
        let rows: JSX.Element[] = []

        // return (
        //     <tbody>
        //         {renderLoadingRow()}
        //         <tr className='h-2' />
        //         {renderLoadingRow()}
        //         <tr className='h-2' />
        //         {renderLoadingRow()}
        //     </tbody>
        // )
        if (!incentivePoolsData || incentivePoolsData.length === 0)
            return (
                <tbody>
                    {renderLoadingRow()}
                    <tr className='h-2' />
                    {renderLoadingRow()}
                    <tr className='h-2' />
                    {renderLoadingRow()}
                </tbody>
            )
        rows = incentivePoolsData.map((item, index) => renderTableRow(item, index))

        return <tbody className='shadow-2xl lg:shadow-none rounded-[16px]'>{rows}</tbody>
    }

    function renderPool(pool: PoolOverview & PoolData) {
        return (
            <td className='lg:pl-[50px]  pr-4 lg:pt-[16px] lg:pb-[17px]'>
                <div className='flex items-center'>
                    <img style={{}} src={getIconUrl('mainnet-alpha', pool.token0.address)} alt='' className='w-[16px] h-[16px] lg:h-[24px] lg:w-[24px]' />
                    <img
                        style={{
                            marginRight: 8,
                            marginLeft: -8
                        }}
                        src={getIconUrl('mainnet-alpha', pool.token1.address)}
                        alt=''
                        className='w-[16px] h-[16px] lg:h-[24px] lg:w-[24px]'
                        // width={24}
                        // height={24}
                    />
                    <span className='text-[12px] lg:text-[18px]'>
                        {getToken1Name(pool)}/{getToken2Name(pool)}
                    </span>
                    <div className='flex-shrink-0 ml-2 text-[#0AE8F9] bg-[#0C3F6D] p-1 rounded-xl text-[8px] lg:text-[16px]'>{pool.pool_fee + '%'}</div>
                    {renderIncentiveBadge(pool.token0.address, pool.token1.address)}
                </div>
            </td>
        )
    }

    function renderTVL(poolData: PoolOverview & PoolData) {
        return (
            <td className='hidden lg:inline lg:pb-[17px] lg:pt-[16px]'>
                <div className='lg:flex flex-col pr-4'>
                    <span className='text-[18px]'>${formatDollarValue(poolData.tvl ?? '0')}</span>
                    {poolData.tvl_change_24h ? (
                        <div className='flex'>
                            <span className='bg-[#0C3F6D] rounded-[8px] px-2 py-1 flex'>
                                <PrecentChange className='mr-0' fontSize={12} lgFontSize={12} value={new BigNumber(poolData.tvl_change_24h || 0)} />
                            </span>
                        </div>
                    ) : null}
                </div>
            </td>
        )
    }

    function renderAPR(poolData: PoolOverview & PoolData) {
        let apr = (poolData && poolData.apr && poolData.apr.apr_cl ? poolData.apr.apr_cl : 0) as number
        if (poolData.apr.incentive_apr) apr = apr + poolData.apr.incentive_apr
        return (
            <td className='lg:pb-[17px] lg:pt-[16px] lg:pr-[70px]'>
                <div className='flex flex-col relative'>
                    <span className='lg:w-[80px] text-[12px] lg:text-[18px]'>{formatNumber(apr ? apr : 0)}%</span>
                    {/* <div className='hidden lg:flex w-[1px] bg-white h-[40px] mr-2' /> */}
                    <div className='hidden lg:flex'>
                        <div className='flex text-[12px] items-center mr-4'>
                            <span className='text-white font-[500] flex-shrink-0 mr-2'>Pool APR</span>
                            <span className='bg-[#0C3F6D] rounded-[8px] px-2 py-1'>{formatNumber(poolData.apr && poolData.apr.apr_cl ? poolData.apr.apr_cl : 0) + '%'}</span>
                        </div>
                        <div className='flex text-[#0AE8F9] text-[12px] items-center'>
                            <span className=' font-[500]'>Defi Spring APR</span>
                            <span className='ml-2  bg-[#0C3F6D] font-bold rounded-[8px] py-1 px-2'>
                                {formatNumber(poolData.apr && poolData.apr.incentive_apr ? poolData.apr.incentive_apr : 0) + '%'}
                            </span>
                        </div>
                    </div>
                </div>
            </td>
        )
    }

    function renderClaim(poolData: PoolOverview & PoolData) {
        const isOpen = openAPRList[`${poolData.token0.address}_${poolData.token1.address}`]

        return (
            <td className='lg:pb-[17px] lg:pt-[16px] pr-4'>
                <div className='flex items-center'>
                    <PrimaryButton
                        className='mr-2 w-[96px] lg:w-[146px] h-[24px]'
                        onClick={() => {
                            if (!network || network !== 'mainnet-alpha') return setIsWrongNetworkPopupActive()
                            const ename = 'mint_add_position_from_rewards'
                            GAtrack(ename, ename, ename)
                            navigate(`/create-position?token0=${poolData.token0.address}&token1=${poolData.token1.address}&default_fee=${poolData.fee || 0.05}`)
                        }}
                        size='sm'
                        theme='filled'>
                        <span className='text-[10px] lg:text-[14px]'>Add Liquidity</span>
                    </PrimaryButton>
                    {poolData &&
                    poolsList[`${poolData.token0.address}_${poolData.token1.address}`] &&
                    poolsList[`${poolData.token0.address}_${poolData.token1.address}`].length > 1 &&
                    mainPools.includes(poolData.poolkey) ? (
                        <div
                            style={{ background: 'rgba(255, 255, 255, 0.10)' }}
                            onClick={() => {
                                setOpenAPRList({ ...openAPRList, [`${poolData.token0.address}_${poolData.token1.address}`]: !isOpen || false })
                            }}
                            className='cursor-pointer flex lg:justify-between justify-center items-center rounded-full flex-shrink-0 w-[24px] lg:w-auto  h-[24px] lg:h-auto lg:rounded-[30px] lg:py-2 lg:px-[14px] text-[14px] text-white'>
                            <span className='hidden lg:flex'>Check APRs</span>
                            {isOpen ? (
                                <XIcon className='flex-shrink-0 w-[10px] h-[10px] lg:h-[14px] lg:w-[14px] lg:ml-1' />
                            ) : (
                                <ArrowDown className='flex-shrink-0 lg:h-[14px] lg:w-[14px] w-[10px] h-[10px] lg:ml-1' />
                            )}
                        </div>
                    ) : null}
                </div>
            </td>
        )
    }

    function renderTableRow(poolData: PoolOverview & PoolData, index: number) {
        const isOpen = openAPRList && openAPRList[`${poolData.token0.address}_${poolData.token1.address}`]
        // if (isOpen) r
        return (
            <>
                {index !== 0 && isOpen && mainPools.includes(poolData.poolkey) ? <tr className='h-1'></tr> : null}
                <tr
                    key={poolData.poolkey}
                    style={{ background: isOpen ? 'rgba(255, 255, 255, 0.10)' : '' }}
                    className={`border-[#0C3F6D] border-b-[1px] lg:pl-[50px] pl-[16px] py-[18px] pr-[32px] h-[70px] px-[30px] lg:pt-[16px] lg:pb-[17px]`}>
                    {renderPool(poolData)}
                    {renderTVL(poolData)}
                    {renderAPR(poolData)}
                    {renderClaim(poolData)}
                </tr>
            </>
        )
    }
    function renderDataTable() {
        return (
            <table className='bg-transparent z-[20] tablerounded w-full mt-5'>
                {renderTableHeader()}
                {renderTableBody()}
            </table>
        )
    }

    return <>{renderDataTable()}</>
}
